import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cmitLogo from '../../assets/cmit-logo.png';
import actionsIcon from '../../assets/review/actions.png';
import alarmIcon from '../../assets/review/alarm.png';
import assetsIcon from '../../assets/review/assets.png';
import checkMark from '../../assets/review/check-mark.png';
import cmitLogoLight from '../../assets/review/cmit-logo-light.png';
import discussionRed from '../../assets/review/discussion-red.png';
import securityDiscussion from '../../assets/review/security-discussion.png';
import discussionIcon from '../../assets/review/discussion.png';
import itMark from '../../assets/review/it-mark.png';
import newProductIcon from '../../assets/review/new-product.png';
import newServiceIcon from '../../assets/review/new-service.png';
import projectsIcon from '../../assets/review/projects.png';
import securityIcon from '../../assets/review/security.png';
import servicesIcon from '../../assets/review/services.png';
import supportIcon from '../../assets/review/support.png';
import thankYouArt from '../../assets/review/thank-you.jpg';
import warrantyIcon from '../../assets/review/warranty.png';
import whatsNewIcon from '../../assets/review/whats-new.png';
import { Gauge } from '../../components/Gauge';
import { Segmented } from '../../components/data';
import { TicketBarsCard, TicketTrendChart } from './reviewTicketCharts';
import { CsatDial, CsatTable, csatWindow, loadCsat, type CsatSnap } from './CsatPanel';
import { loadServicesProvided, ServicesGrid, type ServicesSnap } from './ServicesProvided';
import { loadActiveProjects, ProjectsList, type ProjectsSnap } from './ActiveProjects';
import {
  AgentAttentionList,
  AgentHealthEmpty,
  AgentHealthFigures,
  loadAgentHealth,
  type AgentHealthSnap,
} from './AgentHealth';
import { assetPages, AssetsTable, loadReviewAssets, type AssetLine, type AssetsSnap } from './AssetSlides';
import { daysUntil, dialFor, KINDS, loadWarranties, shortDay, stateOf, type Kind, type WarrantiesSnap } from './AmWarranties';
import { CapturedNote, SnapshotContext, capturedLabel, useCaptured, type Snap, type SnapshotPart } from './reviewSnapshot';
import { RoseChart, RoseLegend, type RoseFamily } from '../../components/RoseChart';
import { Empty, ErrorBanner, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuthedImage, useResource } from '../../lib/hooks';
import {
  bucketTotals,
  costSummaryText,
  isOpenRec,
  priorityRank,
  topPriority,
} from '../../lib/amCosts';
import { scoreAudit, type ControlStatus } from '../../lib/amRag';
import { formatDate, formatNumber } from '../../lib/format';
import { filledActionItems, parseActionItems } from '../../lib/actionItems';
import { DISCUSSION_COLUMNS, filled, parseDiscussion } from '../../lib/businessDiscussion';
import {
  BACKUP_SLOTS,
  imagePath,
  parseSlideImages,
  SERVICE_SLOTS,
  FEEDBACK_SLOTS,
  SUPPORT_SLIDE_SLOTS,
  type SavedSlideImage,
  type SlideImageSlot,
} from '../../lib/slideImages';
import { parseSupportSlides, supportSlideView } from '../../lib/supportSlides';
import { parseTakeaways } from '../../lib/supportTakeaways';
import { filledItems, parseWhatsNew, type WhatsNewKey } from '../../lib/whatsNew';
import {
  parseIdList,
  type AmAuditDetail,
  type AmClientDetail,
  type AmSbr,
  type ComplianceAssessment,
  type ResultStatus,
  type Me,
  type AmRecommendation,
} from '../../lib/types';
import {
  ControlDomains,
  ControlSplit,
  PriorityBadge,
  ScoreDial,
} from './amUi';

type Decision = 'Approved' | 'Declined' | 'Proposed';

/**
 * Presenting a business review.
 *
 * A deck, not a report: cover, account overview, one scorecard per audit
 * reviewed, then the recommendations — and that last slide is the point. The
 * client's Approve / Decline decision is captured live, in the meeting, while
 * they are in the room to make it. Writing decisions down afterwards from memory
 * is how a review turns into three weeks of email.
 *
 * Finishing writes those decisions to the recommendations and hands over to the
 * follow-up, so the recap and the sign-off form already know what was decided.
 */
export function AmSbrPresentation({
  client,
  sbr,
  fullScreen = false,
  printAll = false,
  onExit,
  onFinished,
}: {
  client: AmClientDetail;
  sbr: AmSbr;
  /** Open in Presentation mode: the deck fills the whole window. */
  fullScreen?: boolean;
  /** Every slide, one after another, for printing or saving as a PDF — the
   *  copy of the presentation the follow-up sends. */
  printAll?: boolean;
  onExit: () => void;
  onFinished: () => void;
}) {
  /* Presentation mode: the slide alone, covering the whole window (and the
     screen, where the browser allows it), scaled to fit — see usePresenting.
     Esc or the Exit control returns to the normal view. */
  const [presenting, setPresenting] = useState(fullScreen);
  const auditIds = useMemo(() => parseIdList(sbr.AuditIds), [sbr.AuditIds]);
  const recIds = useMemo(() => parseIdList(sbr.RecIds), [sbr.RecIds]);
  const stakeholderIds = useMemo(() => parseIdList(sbr.StakeholderIds), [sbr.StakeholderIds]);

  const attendees = client.stakeholders.filter((s) => stakeholderIds.includes(s.AmStakeholderId));

  /* The review's snapshot (migration 150): what this deck showed, kept on the
     review. Read once; each part is captured the first time it is needed and
     shown from then on, until a Refresh. See reviewSnapshot.tsx. */
  const me = useResource<Me>('/me');
  const canWrite = me.data ? me.data.accessLevel !== 'viewer' : false;
  const snapsRes = useResource<Record<string, Snap>>(`/am/sbrs/${sbr.AmSbrId}/snapshots`);
  const [snaps, setSnaps] = useState<Record<string, Snap> | null>(null);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (snaps !== null) return;
    if (snapsRes.data) setSnaps(snapsRes.data);
    /* No snapshot to be had (the read failed): show live data, keep nothing. */
    else if (snapsRes.error) setSnaps({});
  }, [snapsRes.data, snapsRes.error, snaps]);
  /* Saves go one at a time: the deck captures several parts as it opens, and
     the database refuses two writes to the same review's snapshot at once. */
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const saveSnap = useCallback(
    (part: SnapshotPart, payload: unknown): Promise<void> => {
      const run = saveQueue.current
        .catch(() => undefined)
        .then(async () => {
          const saved = await api.put<Snap>(`/am/sbrs/${sbr.AmSbrId}/snapshots/${part}`, { payload });
          setSnaps((cur) => ({ ...(cur ?? {}), [part]: saved }));
        });
      saveQueue.current = run;
      return run;
    },
    [sbr.AmSbrId],
  );
  const ready = snaps !== null && me.data !== null;

  /* The recommendations as they were when the review was first opened — not
     as they are now — so a recommendation decided since still reads the way it
     was presented. Captured on first open; updated with the decisions on Finish. */
  const recSource = useMemo(
    () => (snaps?.recommendations?.payload as AmRecommendation[] | undefined) ?? client.recommendations,
    [snaps, client.recommendations],
  );
  useEffect(() => {
    if (!ready || !canWrite || snaps?.recommendations) return;
    void saveSnap('recommendations', client.recommendations).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, canWrite, generation]);

  /* The recommendations this meeting is about: everything the review's audits
     have produced, plus anything attached to the review directly. Read from
     the audits as they are now, not only from RecIds — that list is filled
     when an audit is ticked on the form, so a recommendation added to the
     audit afterwards would otherwise never reach the deck. */
  const meetingRecs = useMemo(
    () =>
      recSource.filter(
        (r) =>
          recIds.includes(r.AmRecommendationId) ||
          (r.AmAuditId !== null && auditIds.includes(r.AmAuditId)),
      ),
    [recSource, recIds, auditIds],
  );

  const [audits, setAudits] = useState<AmAuditDetail[] | null>(null);
  /* The Security Maturity slide's assessment, when the review names one. */
  const [security, setSecurity] = useState<ComplianceAssessment | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  /* The thank-you slide's email when the review gives none: the account
     manager's, from the directory. */
  const manager = useResource<{ Email: string | null }>(
    client.AccountManagerEmployeeId ? `/employees/${client.AccountManagerEmployeeId}` : null,
  );
  const managerEmail = manager.data?.Email ?? null;
  const [slide, setSlide] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The scorecards need each audit's answers, which the client payload does not
     carry — it has counts, not responses. One request per audit reviewed, and
     an audit that fails to load is dropped rather than blocking the deck.

     Kept in the snapshot with the list of audits it covers: the same audits
     are shown as captured; a different selection on the review is read again. */
  useEffect(() => {
    if (!ready) return;
    let live = true;
    const key = [...auditIds].sort().join(',');
    const held = snaps?.audits?.payload as { key: string; audits: AmAuditDetail[] } | undefined;
    if (held && held.key === key) {
      setAudits(held.audits);
      return;
    }
    Promise.all(
      auditIds.map((id) => api.get<AmAuditDetail>(`/am/audits/${id}`).catch(() => null)),
    ).then((results) => {
      if (!live) return;
      const list = results.filter((a): a is AmAuditDetail => a !== null);
      setAudits(list);
      if (canWrite) void saveSnap('audits', { key, audits: list }).catch(() => undefined);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditIds, ready, generation]);

  /* One fetch, and a failure — the assessment deleted or archived since —
     leaves the slide out rather than holding up the deck. Kept in the snapshot
     like the audits, keyed by the assessment the review names. */
  useEffect(() => {
    if (!ready) return;
    let live = true;
    const id = sbr.SecurityAssessmentId;
    if (!id) {
      setSecurity(null);
      return;
    }
    const held = snaps?.security?.payload as { key: string; assessment: ComplianceAssessment } | undefined;
    if (held && held.key === String(id)) {
      setSecurity(held.assessment);
      return;
    }
    api
      .get<ComplianceAssessment>(`/am/clients/${client.AmClientId}/compliance/${id}`)
      .then((a) => {
        if (!live) return;
        setSecurity(a);
        if (canWrite) void saveSnap('security', { key: String(id), assessment: a }).catch(() => undefined);
      })
      .catch(() => live && setSecurity(null));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbr.SecurityAssessmentId, client.AmClientId, ready, generation]);

  /* The Asset Management slides' assets, captured as the deck opens rather
     than when the slide is reached: how many pages they take decides how many
     slides there are. Kept in the snapshot; Refresh on the slide reads again. */
  const [assetsSnap, setAssetsSnap] = useState<AssetsSnap | null>(null);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [assetsBusy, setAssetsBusy] = useState(false);
  useEffect(() => {
    if (!ready || sbr.ShowAssetManagement === false) return;
    const held = snaps?.assets?.payload as AssetsSnap | undefined;
    if (held) {
      setAssetsSnap(held);
      return;
    }
    let live = true;
    setAssetsSnap(null);
    setAssetsError(null);
    loadReviewAssets(client.AmClientId, false)
      .then((d) => {
        if (!live) return;
        setAssetsSnap(d);
        if (canWrite && d.linked) void saveSnap('assets', d).catch(() => undefined);
      })
      .catch((e: unknown) => live && setAssetsError(e instanceof Error ? e.message : 'Could not read the assets'));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, generation, sbr.ShowAssetManagement, client.AmClientId]);
  async function refreshAssets() {
    if (!window.confirm('Replace the assets captured for this review with what is in Autotask now? The list from the day of the review will be gone.')) return;
    setAssetsBusy(true);
    setAssetsError(null);
    try {
      const d = await loadReviewAssets(client.AmClientId, true);
      setAssetsSnap(d);
      if (d.linked) await saveSnap('assets', d);
    } catch (e) {
      setAssetsError(e instanceof Error ? e.message : 'Could not read the assets');
    } finally {
      setAssetsBusy(false);
    }
  }
  const assetPageList = useMemo(() => (assetsSnap?.linked ? assetPages(assetsSnap.assets) : []), [assetsSnap]);

  useEffect(() => {
    const initial: Record<string, Decision> = {};
    for (const rec of meetingRecs) {
      initial[rec.AmRecommendationId] =
        rec.Status === 'Declined' ? 'Declined' : rec.Status === 'Approved' ? 'Approved' : 'Proposed';
    }
    setDecisions(initial);
  }, [meetingRecs]);

  /* The "What's new" groups that have anything in them, each with only its
     filled-in lines, in slide order. Empty means no slide. */
  const whatsNew = useMemo(() => {
    const parsed = parseWhatsNew(sbr.WhatsNew);
    return WHATS_NEW.map((w) => ({ ...w, items: filledItems(parsed[w.key]) })).filter(
      (w) => w.items.length > 0,
    );
  }, [sbr.WhatsNew]);

  /* The Business Discussion columns that have anything in them, each with
     only its filled-in lists. Empty means no slide. */
  const discussion = useMemo(() => {
    const parsed = parseDiscussion(sbr.BusinessDiscussion);
    return DISCUSSION_COLUMNS.map((col) => ({
      heading: col.heading,
      lists: col.lists
        .map((l) => ({ ...l, items: filled(parsed[l.key]) }))
        .filter((l) => l.items.length > 0),
    })).filter((col) => col.lists.length > 0);
  }, [sbr.BusinessDiscussion]);

  /* What goes in the Action Items boxes, most urgent first: the meeting's
     recommendations (above). When the review names no audit and no
     recommendation, the client's audit recommendations stand in — the slide
     is "what we recommend", and an empty one is no use to anybody.

     Open ones only (proposed, approved, in progress): a completed or declined
     recommendation is not an action item. A stable sort, so equal priorities
     keep the order the client's recommendation list has them in. */
  const actionItems = useMemo(() => {
    const pool =
      meetingRecs.length > 0
        ? meetingRecs
        : recSource.filter((r) => r.AmAuditId !== null);
    return pool
      .filter((r) => isOpenRec(r.Status))
      .map((rec, i) => ({ rec, i }))
      .sort((a, b) => priorityRank(topPriority(b.rec)) - priorityRank(topPriority(a.rec)) || a.i - b.i)
      .map(({ rec }) => rec);
  }, [meetingRecs, recSource]);

  /* The boxes on the Action Items slide: the audit recommendations above, then
     the review's own action items in the order they were entered (135). */
  const actionBoxes = useMemo<ActionBox[]>(
    () => [
      ...actionItems.map((rec) => {
        const text = rec.Description?.trim() || rec.Finding?.trim() || '';
        return {
          key: rec.AmRecommendationId,
          title: rec.Title || 'Recommendation',
          text: text ? shortText(text) : costSummaryText(rec),
        };
      }),
      ...filledActionItems(parseActionItems(sbr.ActionItems)).map((item, i) => ({
        key: `own-${i}`,
        title: item.title.trim() || 'Action item',
        text: shortText(item.detail),
      })),
    ],
    [actionItems, sbr.ActionItems],
  );

  /* The uploaded screenshots (138) and the Service Information talking points (139). */
  const slideImages = useMemo(() => parseSlideImages(sbr.SlideImages), [sbr.SlideImages]);
  const backupShots = useMemo(() => BACKUP_SLOTS.filter((slot) => slideImages[slot]), [slideImages]);
  const backupTakeaways = useMemo(
    () => parseTakeaways(sbr.BackupTakeaways).map((t) => t.trim()).filter(Boolean),
    [sbr.BackupTakeaways],
  );
  const serviceShots = useMemo(
    () => SERVICE_SLOTS.filter((slot) => slideImages[slot]),
    [slideImages],
  );
  const feedbackShots = useMemo(() => FEEDBACK_SLOTS.filter((slot) => slideImages[slot]), [slideImages]);
  const servicePoints = useMemo(
    () => parseTakeaways(sbr.ServiceTalkingPoints).map((t) => t.trim()).filter(Boolean),
    [sbr.ServiceTalkingPoints],
  );
  /* The Technical Support slides the review added itself (202), in the order
     they were added. One slide each, whether or not anything was filled in. */
  const addedSupport = useMemo(
    () => parseSupportSlides(sbr.SupportSlides).map((slide) => ({ pair: slide.pair, ...supportSlideView(slide) })),
    [sbr.SupportSlides],
  );

  const slides = useMemo(() => {
    const list: (
      | { type: 'audit'; auditId: string }
      | { type: 'actions'; page: number }
      | { type: 'assets'; page: number }
      | { type: 'supportAdded'; index: number }
      | 'cover'
      | 'agenda'
      | 'whatsnew'
      | 'discussion'
      | 'support'
      | 'tickets'
      | 'backup'
      | 'service'
      | 'feedback'
      | 'assetOverview'
      | 'agentHealth'
      | 'security'
      | 'servicesProvided'
      | 'activeProjects'
      | 'recommendations'
      | 'wrapup'
      | 'questions'
      | 'thankyou'
    )[] = ['cover', 'agenda'];
    /* Only when the review form says something is new — see migration 132. */
    if (whatsNew.length > 0) list.push('whatsnew');
    /* Likewise — see migration 133. */
    if (discussion.length > 0) list.push('discussion');
    /* The two Technical Support slides, each switched on on the review (137). */
    if (sbr.ShowSupportRequests) list.push('support');
    if (sbr.ShowTicketSummary) list.push('tickets');
    /* Then the Technical Support slides this review added itself (202), in
       the order they were added — under the same heading, after the two the
       deck reads from Autotask. */
    for (let index = 0; index < addedSupport.length; index++) list.push({ type: 'supportAdded', index });
    /* Only when there is a backup screenshot or a description — see 140. */
    if (backupShots.length > 0 || backupTakeaways.length > 0) list.push('backup');
    /* Only when there is a service screenshot or a talking point — see 139. */
    if (serviceShots.length > 0 || servicePoints.length > 0) list.push('service');
    /* User Feedback: the client's CSAT from Autotask's ticket survey, unless the
       review turned it off (153) — uploaded screenshots stand in only when
       the client is not linked. */
    if (sbr.ShowUserFeedback !== false) list.push('feedback');
    /* Slide 10, Asset Management: first the template's page — warranty dials,
       Needs Attention, Hardware Recommendations — then the client's active
       assets by category, a slide per page of them (at least one) — unless
       turned off (154). */
    if (sbr.ShowAssetManagement !== false) {
      list.push('assetOverview');
      const n = Math.max(1, assetPageList.length);
      for (let page = 0; page < n; page++) list.push({ type: 'assets', page });
    }
    /* Slide 10a, Agent Health: how many of the client's machines are
       checking in, patched and protected, live from Automate through the
       client link (185) — unless turned off (186). Straight after Asset
       Management, because the two answer what the client has and whether it
       is well. */
    if (sbr.ShowAgentHealth !== false) list.push('agentHealth');
    /* Slide 11, Services Provided: the services on the client's active
       Autotask contracts, names and units only — unless turned off (153). */
    if (sbr.ShowServicesProvided !== false) list.push('servicesProvided');
    /* Slide 12, Security Maturity: only when the review picked an assessment
       and it loaded — see 134. */
    if (security) list.push('security');
    /* Slide 13, Active IT Projects: the client's open Autotask projects —
       unless turned off (153). */
    if (sbr.ShowActiveProjects !== false) list.push('activeProjects');
    for (const audit of audits ?? []) list.push({ type: 'audit', auditId: audit.AmAuditId });
    /* The CMIT "Action Items/Recommendations" slide, six to a page, ahead of
       the slide where they are decided. Left out when nothing is attached. */
    for (let page = 0; page * ACTIONS_PER_PAGE < actionBoxes.length; page++) {
      list.push({ type: 'actions', page });
    }
    /* The template's last two pages: Questions?, then Thank you. */
    list.push('recommendations', 'wrapup', 'questions', 'thankyou');
    return list;
  }, [
    audits,
    whatsNew.length,
    discussion.length,
    security,
    actionBoxes.length,
    sbr.ShowSupportRequests,
    sbr.ShowTicketSummary,
    addedSupport.length,
    sbr.ShowUserFeedback,
    sbr.ShowServicesProvided,
    sbr.ShowActiveProjects,
    sbr.ShowAssetManagement,
    sbr.ShowAgentHealth,
    assetPageList.length,
    slideImages,
    backupShots.length,
    backupTakeaways,
    serviceShots.length,
    servicePoints.length,
    feedbackShots.length,
  ]);

  const go = useCallback(
    (delta: number) => setSlide((s) => Math.max(0, Math.min(slides.length - 1, s + delta))),
    [slides.length],
  );

  /* Arrow keys, because nobody drives a deck with a mouse in front of a client. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') go(1);
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  async function finish() {
    setFinishing(true);
    setError(null);
    try {
      for (const rec of meetingRecs) {
        const decision = decisions[rec.AmRecommendationId];
        if (decision && decision !== rec.Status) {
          await api.put(`/am/recommendations/${rec.AmRecommendationId}`, { status: decision });
        }
      }
      if (canWrite) {
        const decided = new Set(meetingRecs.map((r) => r.AmRecommendationId));
        await saveSnap(
          'recommendations',
          recSource.map((r) => {
            const decision = decisions[r.AmRecommendationId];
            return decided.has(r.AmRecommendationId) && decision && decision !== r.Status ? { ...r, Status: decision } : r;
          }),
        ).catch(() => undefined);
      }
      onFinished();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the decisions');
      setFinishing(false);
    }
  }

  if (audits === null || !ready) return <Loading label="Preparing the review" />;

  /* Throw the snapshot away and capture everything again — the slides that
     read Autotask do so the next time they are shown. */
  async function refreshAll() {
    if (!window.confirm('Replace everything captured for this review with what is there now? The figures from the day of the review will be gone.')) return;
    await api.del(`/am/sbrs/${sbr.AmSbrId}/snapshots`);
    setSnaps({});
    setAudits(null);
    setSecurity(null);
    setGeneration((g) => g + 1);
  }
  const firstCapture = Object.values(snaps ?? {}).reduce<Snap | null>(
    (a, b) => (a === null || String(b.capturedUtc) < String(a.capturedUtc) ? b : a),
    null,
  );

  const current = slides[slide];
  const decided = meetingRecs.filter(
    (r) => decisions[r.AmRecommendationId] === 'Approved' || decisions[r.AmRecommendationId] === 'Declined',
  ).length;

  /* One slide. The deck shows the current one; the print copy shows them all. */
  const renderSlide = (current: (typeof slides)[number] | undefined) => (
    <>

      {current === 'cover' && (
        <div className="am-slide am-slide-cover">
          {/* The CMIT title-slide template: blue field, a navy triangle in two
              corners, the logo over a short red rule, the copyright line at
              the foot. Only the look; what the slide says is unchanged. */}
          <span className="am-cover-corner am-cover-corner-tr" aria-hidden="true" />
          <span className="am-cover-corner am-cover-corner-bl" aria-hidden="true" />
          <img className="am-cover-logo" src={cmitLogo} alt="CMIT Solutions — Your Technology Team" />
          <span className="am-cover-rule" aria-hidden="true" />
          <div>
            <span className="eyebrow">{sbr.Cadence ?? 'Business'} review</span>
            <h1>{sbr.Title ?? 'Business review'}</h1>
            <p>{client.Name}</p>
            <p className="mt-4">{formatDate(sbr.NextDate)}</p>
            {attendees.length > 0 && (
              <p className="mt-4">Attending: {attendees.map((a) => a.Name).join(', ')}</p>
            )}
          </div>
          <p className="am-cover-foot">© CMIT Solutions LLC {new Date().getFullYear()}</p>
        </div>
      )}

      {current === 'agenda' && (
        <div className="am-slide am-slide-agenda">
          <span className="am-agenda-corner" aria-hidden="true" />
          <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
          <h2 className="am-agenda-title">Welcome to your Business Review</h2>
          <div className="am-agenda-body">
            <aside className="am-agenda-side">
              <img src={itMark} alt="" />
              <h3>Business Review Agenda</h3>
              <p>CMIT Solutions business reviews are intended to:</p>
              <ul>
                <li>Evaluate the current state of support and technology</li>
                <li>Review ongoing business initiatives related to technology</li>
                <li>Evaluate technology products and services.</li>
              </ul>
            </aside>
            <div className="am-agenda-items">
              {AGENDA.map((item) => (
                <div key={item.title} className="am-agenda-item">
                  <span className="am-agenda-icon">
                    <img src={item.icon} alt="" />
                  </span>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {current === 'whatsnew' && (
        <div className="am-slide am-slide-agenda">
          <span className="am-agenda-corner" aria-hidden="true" />
          <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
          <h2 className="am-agenda-title">What’s new at CMIT Solutions</h2>
          <div className="am-agenda-body">
            <aside className="am-agenda-side">
              <img src={checkMark} alt="" />
              <h3>Check it out.</h3>
              <p>Sharing news regarding new or updated products, services, our teams and our business.</p>
            </aside>
            <div className="am-news">
              {whatsNew.map((w) => (
                <section key={w.key} className="am-news-group">
                  <h3>{w.heading}</h3>
                  {w.items.map((item, i) => (
                    <div key={i} className="am-agenda-item">
                      <span className="am-agenda-icon">
                        <img src={w.icon} alt="" />
                      </span>
                      <div>
                        <h4>{item.name || w.fallback}</h4>
                        {item.detail && <p>{item.detail}</p>}
                      </div>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      {current === 'discussion' && (
        <div className="am-slide am-slide-agenda">
          <span className="am-agenda-corner" aria-hidden="true" />
          <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
          <h2 className="am-agenda-title">Business Discussion</h2>
          <div className="am-agenda-body">
            <aside className="am-agenda-side am-discuss-side">
              <img src={discussionRed} alt="" />
              <ul>
                <li>Administrative</li>
                <li>Key Activity</li>
                <li>Other</li>
              </ul>
            </aside>
            <div className="am-discuss">
              {discussion.map((col) => (
                <div key={col.heading} className="am-discuss-col">
                  <h3>{col.heading}</h3>
                  {col.lists.map((l) => (
                    <section key={l.key}>
                      <h4>{l.label}</h4>
                      <ol>
                        {l.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ol>
                    </section>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {current === 'support' && (
        <SupportSlide
          clientId={client.AmClientId}
          takeaways={parseTakeaways(sbr.SupportTakeaways).map((t) => t.trim()).filter(Boolean)}
        />
      )}

      {current === 'tickets' && <TicketSummarySlide clientId={client.AmClientId} />}

      {current && typeof current === 'object' && current.type === 'supportAdded' && (
        <AddedSupportSlide sbrId={sbr.AmSbrId} slide={addedSupport[current.index]} images={slideImages} />
      )}

      {current === 'backup' && (
        <BackupSlide
          sbrId={sbr.AmSbrId}
          shots={backupShots.map((slot) => ({ slot, ...slideImages[slot]! }))}
          takeaways={backupTakeaways}
        />
      )}

      {current === 'feedback' && (
        <FeedbackSlide
          sbrId={sbr.AmSbrId}
          clientId={client.AmClientId}
          main={slideImages.feedback ? { slot: 'feedback', ...slideImages.feedback } : null}
          side={slideImages.feedback2 ? { slot: 'feedback2', ...slideImages.feedback2 } : null}
        />
      )}

      {current === 'service' && (
        <ServiceSlide
          sbrId={sbr.AmSbrId}
          shots={serviceShots.map((slot) => ({ slot, ...slideImages[slot]! }))}
          points={servicePoints}
        />
      )}

      {current === 'security' && security && <SecuritySlide assessment={security} />}

      {current && typeof current === 'object' && current.type === 'assets' && (
        <AssetManagementSlide
          snap={assetsSnap}
          lines={assetPageList[current.page] ?? []}
          page={current.page}
          pages={Math.max(1, assetPageList.length)}
          error={assetsError}
          note={
            <CapturedNote
              capturedUtc={snaps?.assets?.capturedUtc ?? null}
              capturedBy={snaps?.assets?.capturedBy ?? null}
              canWrite={canWrite}
              refreshing={assetsBusy}
              onRefresh={() => void refreshAssets()}
            />
          }
        />
      )}

      {current === 'assetOverview' && <AssetOverviewSlide clientId={client.AmClientId} />}

      {current === 'agentHealth' && (
        <AgentHealthSlide
          clientId={client.AmClientId}
          sbrId={sbr.AmSbrId}
          shot={slideImages.agentHealth ? { slot: 'agentHealth', ...slideImages.agentHealth } : null}
        />
      )}

      {current === 'servicesProvided' && <ServicesProvidedSlide clientId={client.AmClientId} />}

      {current === 'activeProjects' && <ActiveProjectsSlide clientId={client.AmClientId} />}

      {current && typeof current === 'object' && current.type === 'audit' && (
        <AuditSlide audit={audits.find((a) => a.AmAuditId === current.auditId)} />
      )}

      {typeof current === 'object' && current.type === 'actions' && (
        <ActionItemsSlide
          items={actionBoxes}
          page={current.page}
          pages={Math.ceil(actionBoxes.length / ACTIONS_PER_PAGE)}
        />
      )}

      {current === 'recommendations' && (
        <div className="am-slide">
          <h2 className="am-slide-h">
            Recommendations{' '}
            <span className="muted" style={{ fontSize: 'var(--text-base)' }}>
              {decided} of {meetingRecs.length} decided
            </span>
          </h2>
          {meetingRecs.length === 0 ? (
            <Empty title="Nothing attached">
              <p>
                This review has no recommendations against it. Attach them when you schedule the
                meeting, so the decisions can be captured here.
              </p>
            </Empty>
          ) : (
            meetingRecs.map((rec) => {
              const decision = decisions[rec.AmRecommendationId] ?? 'Proposed';
              return (
                <div
                  key={rec.AmRecommendationId}
                  className={`am-decide${decision === 'Approved' ? ' approved' : decision === 'Declined' ? ' declined' : ''}`}
                >
                  <div className="grow">
                    <div className="row wrap">
                      <span className="strong">{rec.Title}</span>
                      <PriorityBadge priority={topPriority(rec)} />
                      <span className="xs muted">{costSummaryText(rec)}</span>
                    </div>
                    {rec.Finding && <div className="xs muted mt-2">Finding: {rec.Finding}</div>}
                    {rec.Description && <div className="xs muted mt-2">{rec.Description}</div>}
                  </div>
                  <div className="am-decide-actions">
                    {(['Approved', 'Declined', 'Proposed'] as Decision[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`${option === 'Approved' ? 'approve' : option === 'Declined' ? 'decline' : 'pending'}${
                          decision === option ? ' on' : ''
                        }`}
                        onClick={() =>
                          setDecisions((d) => ({ ...d, [rec.AmRecommendationId]: option }))
                        }
                      >
                        {option === 'Proposed' ? 'Pending' : option === 'Approved' ? 'Approve' : 'Decline'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {current === 'questions' && <QuestionsSlide />}

      {current === 'thankyou' && (
        <ThankYouSlide
          email={sbr.ClosingEmail?.trim() || managerEmail}
          phone={sbr.ClosingPhone?.trim() || null}
          website={sbr.ClosingWebsite?.trim() || DEFAULT_CLOSING_WEBSITE}
        />
      )}

      {current === 'wrapup' && (
        <div className="am-slide">
          <h2 className="am-slide-h">Where we landed</h2>
          <div className="grid grid-3 mb-4">
            {(
              [
                ['Approved', meetingRecs.filter((r) => decisions[r.AmRecommendationId] === 'Approved')],
                ['Declined', meetingRecs.filter((r) => decisions[r.AmRecommendationId] === 'Declined')],
                [
                  'Still pending',
                  meetingRecs.filter(
                    (r) =>
                      decisions[r.AmRecommendationId] !== 'Approved' &&
                      decisions[r.AmRecommendationId] !== 'Declined',
                  ),
                ],
              ] as const
            ).map(([label, group]) => (
              <div key={label} className="stat">
                <div className="label">{label}</div>
                <div className={`figure${label === 'Declined' && group.length > 0 ? ' accent' : ''}`}>
                  {group.length}
                </div>
                <div className="foot">
                  {formatNumber(bucketTotals(group).oneTime, 'currency')} one-time
                </div>
              </div>
            ))}
          </div>
          <p className="small muted">
            Finishing saves each decision against its recommendation and opens the follow-up: a recap
            email plus a sign-off form pre-filled with what was decided. Declined security items
            become the risk-acceptance items for the client to sign.
          </p>
          <button
            type="button"
            className="btn btn-primary mt-4"
            disabled={finishing}
            onClick={() => void finish()}
          >
            {finishing ? 'Saving…' : 'Finish and prepare the follow-up →'}
          </button>
        </div>
      )}

    </>
  );

  /* The copy of the presentation for the client: every slide, one to a
     landscape page, from the review's snapshot — what was presented on the
     day. Print, or Save as PDF, and attach it to the follow-up email. */
  if (printAll) {
    return (
      <SnapshotContext.Provider value={{ get: (part) => snaps?.[part], save: saveSnap, canWrite, generation }}>
        <div className="am-print-bar no-print">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onExit}>
            ← Back to the follow-up
          </button>
          <div className="grow">
            <strong>{sbr.Title ?? 'Business review'} — {client.Name}</strong>
            <div className="xs muted">
              {slides.length} slides, one to a landscape page. Choose <strong>Save as PDF</strong> as the printer, then attach the file to
              the follow-up email.
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
        </div>
        <div className="am-print-deck">
          {slides.map((sl, i) => (
            <section key={i} className="am-print-page">
              {renderSlide(sl)}
            </section>
          ))}
        </div>
      </SnapshotContext.Provider>
    );
  }

  return (
    <SnapshotContext.Provider value={{ get: (part) => snaps?.[part], save: saveSnap, canWrite, generation }}>
    <div className={`am-deck${presenting ? ' is-presenting' : ''}`}>
      {presenting && <PresentingChrome slide={slide} total={slides.length} onGo={go} onExit={() => setPresenting(false)} />}
      <div className="am-deck-bar no-print">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onExit}>
          ✕ Exit
        </button>
        <div className="am-deck-title">
          {client.Name} · {sbr.Title ?? 'Business review'}
          <div className="xs muted">
            {firstCapture ? capturedLabel(String(firstCapture.capturedUtc), firstCapture.capturedBy) : 'Capturing this review’s data…'}
            {canWrite && firstCapture && (
              <>
                {' · '}
                <button type="button" className="link-button" onClick={() => void refreshAll()}>
                  Refresh data
                </button>
              </>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPresenting(true)}>
            Presentation mode
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={finishing}
            onClick={() => void finish()}
          >
            {finishing ? 'Saving…' : 'Finish and follow up →'}
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <PresentingStage presenting={presenting} slide={slide}>
        {renderSlide(current)}
      </PresentingStage>

      <div className="am-deck-nav no-print">
        <button type="button" className="btn btn-secondary btn-sm" disabled={slide === 0} onClick={() => go(-1)}>
          ← Previous
        </button>
        <div className="am-deck-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`am-deck-dot${i === slide ? ' active' : ''}`}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setSlide(i)}
            />
          ))}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={slide === slides.length - 1}
          onClick={() => go(1)}
        >
          Next →
        </button>
      </div>
    </div>
    </SnapshotContext.Provider>
  );
}

function AuditSlide({ audit }: { audit: AmAuditDetail | undefined }) {
  if (!audit) {
    return (
      <div className="am-slide">
        <Empty title="Audit unavailable">
          <p>This review references an audit that can no longer be loaded.</p>
        </Empty>
      </div>
    );
  }
  const { counts, scored, score } = scoreAudit(audit.answers);
  return (
    <div className="am-slide">
      <h2 className="am-slide-h">
        {audit.Name ?? 'Audit'} <span className="badge badge-neutral">{audit.SetName}</span>
      </h2>
      {scored === 0 ? (
        <p className="small muted">
          This audit records free-text findings rather than control statuses, so there is no
          percentage to show. Its risk ratings are on the recommendations slide.
        </p>
      ) : (
        <>
          <div className="am-score-row">
            <ScoreDial score={score} label="In place" />
            <div className="grow">
              <ControlSplit counts={counts as Record<ControlStatus, number>} />
            </div>
          </div>
          <ControlDomains items={audit.answers} />
        </>
      )}
    </div>
  );
}

/**
 * The CMIT "Technical Support" slide: the template's sidebar, the client's
 * tickets created vs. completed in each of the last six months — live from
 * Autotask — in the middle, and the review's takeaways on the right.
 *
 * Click a month to see its tickets. Months are this browser's, so a ticket
 * completed late on the 31st counts in the month the client lived it.
 */
function SupportSlide({ clientId, takeaways }: { clientId: string; takeaways: string[] }) {
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Technical Support</h2>
      <div className="am-agenda-body">
        <SupportSide />
        <div className="am-support">
          <div className="am-support-main">
            <h3>Support Request Summary</h3>
            <SupportTicketsChart clientId={clientId} />
          </div>
          <div className="am-support-takeaways">
            <h3>Takeaways</h3>
            {takeaways.length > 0 && (
              <ul>
                {takeaways.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ClientTicket {
  id: number;
  ticketNumber: string;
  title: string;
  status: string;
  priority: string;
  issueType: string;
  assignedTo: string | null;
  /** Who at the client raised it, "Last, First"; null for monitoring and automation. */
  contact: string | null;
  created: string | null;
  completed: string | null;
}

const SUPPORT_MONTHS = 6;

/** The last six months in this browser, this one included. */
function supportWindow() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(1);
  start.setMonth(start.getMonth() - (SUPPORT_MONTHS - 1));
  const end = new Date(start);
  end.setMonth(end.getMonth() + SUPPORT_MONTHS);
  return { from: start, to: end };
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthName = (key: string) => {
  const [y, m] = key.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString([], { month: 'long', year: 'numeric' });
};

/** What a ticket slide keeps: the window it covered and what Autotask returned. */
interface TicketSnap {
  from: string;
  to: string;
  linked: boolean;
  companyName?: string | null;
  tickets: ClientTicket[];
}

/** Reads a client's tickets for a window and stamps the window on the result. */
async function loadTickets(clientId: string, w: { from: Date; to: Date }, refresh: boolean): Promise<TicketSnap> {
  const r = await api.get<Omit<TicketSnap, 'from' | 'to'>>(
    `/am/clients/${clientId}/autotask/tickets?${new URLSearchParams({ from: w.from.toISOString(), to: w.to.toISOString(), ...(refresh ? { refresh: '1' } : {}) })}`,
  );
  return { ...r, from: w.from.toISOString(), to: w.to.toISOString() };
}

function SupportTicketsChart({ clientId }: { clientId: string }) {
  /* Captured once, on the review: the six months up to the day it was taken,
     shown as they were from then on — until Refresh. */
  const cap = useCaptured<TicketSnap>('tickets6m', (refresh) => loadTickets(clientId, supportWindow(), refresh), (d) => d.linked);
  const win = useMemo(
    () => (cap.data ? { from: new Date(cap.data.from), to: new Date(cap.data.to) } : supportWindow()),
    [cap.data],
  );
  const data = { loading: cap.loading, error: cap.error, data: cap.data };
  const note = (
    <CapturedNote
      capturedUtc={cap.capturedUtc}
      capturedBy={cap.capturedBy}
      canWrite={cap.canWrite}
      refreshing={cap.refreshing}
      onRefresh={() => void cap.refresh()}
    />
  );
  const [drill, setDrill] = useState<{ month: string; view: 'created' | 'completed' } | null>(null);

  const months = useMemo(() => {
    const keys: string[] = [];
    const d = new Date(win.from);
    for (let i = 0; i < SUPPORT_MONTHS; i++) {
      keys.push(monthKey(d));
      d.setMonth(d.getMonth() + 1);
    }
    const rows = keys.map((k) => ({ month: k, created: 0, completed: 0 }));
    const at = new Map(rows.map((r) => [r.month, r]));
    for (const t of data.data?.tickets ?? []) {
      if (t.created) {
        const r = at.get(monthKey(new Date(t.created)));
        if (r) r.created += 1;
      }
      if (t.completed) {
        const r = at.get(monthKey(new Date(t.completed)));
        if (r) r.completed += 1;
      }
    }
    return rows;
  }, [data.data, win]);

  if (data.loading) {
    return (
      <div className="am-support-chart">
        <div className="am-support-chart-note">Reading tickets from Autotask…</div>
      </div>
    );
  }
  if (data.error || !data.data?.linked) {
    return (
      <div className="am-support-chart">
        <div className="am-support-chart-label">{data.error ? 'Autotask could not be read' : 'Not found in Autotask'}</div>
        <div className="am-support-chart-note">
          {data.error ?? 'This client was not found in Autotask under its name. Import it from Autotask on Account Management so the names match.'}
        </div>
        {note}
      </div>
    );
  }

  const created = months.reduce((n, m) => n + m.created, 0);
  const completed = months.reduce((n, m) => n + m.completed, 0);
  const inMonth = drill
    ? data.data.tickets
        .filter((t) => {
          const when = drill.view === 'created' ? t.created : t.completed;
          return when !== null && monthKey(new Date(when)) === drill.month;
        })
        .sort((a, b) => String(drill.view === 'created' ? b.created : b.completed).localeCompare(String(drill.view === 'created' ? a.created : a.completed)))
    : [];

  return (
    <div className="am-support-live">
      {note}
      <p className="am-support-live-sub">
        {created} created · {completed} completed · last {SUPPORT_MONTHS} months · click a month for its tickets
      </p>
      <TicketTrendChart months={months} onSelect={(month) => setDrill({ month, view: 'created' })} />
      {drill && (
        <Modal title={`${data.data.companyName ?? 'Tickets'} — ${monthName(drill.month)}`} onClose={() => setDrill(null)}>
          <div className="row wrap mb-2">
            <Segmented
              label="Which tickets"
              options={[
                { id: 'created', label: `Created (${months.find((m) => m.month === drill.month)?.created ?? 0})` },
                { id: 'completed', label: `Completed (${months.find((m) => m.month === drill.month)?.completed ?? 0})` },
              ]}
              value={drill.view}
              onChange={(view) => setDrill({ ...drill, view })}
            />
          </div>
          {inMonth.length === 0 ? (
            <p className="muted small">No tickets {drill.view} in {monthName(drill.month)}.</p>
          ) : (
            <div className="table-wrap am-support-drill">
              <table className="table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>{drill.view === 'created' ? 'Created' : 'Completed'}</th>
                  </tr>
                </thead>
                <tbody>
                  {inMonth.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <span className="mono xs">{t.ticketNumber}</span>
                        <div className="xs muted">{t.title}</div>
                      </td>
                      <td>
                        {t.status}
                        {t.issueType && <div className="xs muted">{t.issueType}</div>}
                      </td>
                      <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                      <td className="xs">
                        {new Date((drill.view === 'created' ? t.created : t.completed)!).toLocaleDateString([], {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/**
 * The CMIT "Backup Review" slide: the template's sidebar, up to three
 * screenshots from the backup console with their captions, and the review's
 * takeaways beside them (149).
 */
function BackupSlide({
  sbrId,
  shots,
  takeaways,
}: {
  sbrId: string;
  shots: { slot: SlideImageSlot; title: string; uploadedUtc: string }[];
  takeaways: string[];
}) {
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Backup Review</h2>
      <div className="am-agenda-body">
        <ServicesSide text="Key reports for applications we provide to protect you and your data." />
        <div className={takeaways.length > 0 && shots.length > 0 ? 'am-support' : 'am-support-main'}>
          {shots.length > 0 && (
            <div className={`am-service-shots am-three-shots n${shots.length}`}>
              {shots.map((s) => (
                <ServiceShot key={s.slot} sbrId={sbrId} {...s} />
              ))}
            </div>
          )}
          {takeaways.length > 0 && (
            <div className="am-support-takeaways">
              <h3>Takeaways</h3>
              <ul>
                {takeaways.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The CMIT "Service Information" slide: the sidebar, up to three uploaded
 * screenshots from the security tools with their captions, and the review's
 * takeaways on the right (139).
 */
function ServiceSlide({
  sbrId,
  shots,
  points,
}: {
  sbrId: string;
  shots: { slot: SlideImageSlot; title: string; uploadedUtc: string }[];
  points: string[];
}) {
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Service Information</h2>
      <div className="am-agenda-body">
        <ServicesSide text="More security application data to show how your environment is being protected." />
        <div className={points.length > 0 && shots.length > 0 ? 'am-support' : 'am-support-main'}>
          {shots.length > 0 && (
            <div className={`am-service-shots am-three-shots n${shots.length}`}>
              {shots.map((s) => (
                <ServiceShot key={s.slot} sbrId={sbrId} {...s} />
              ))}
            </div>
          )}
          {points.length > 0 && (
            <div className="am-support-takeaways">
              <h3>Takeaways</h3>
              <ul>
                {points.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceShot({
  sbrId,
  slot,
  title,
  uploadedUtc,
}: {
  sbrId: string;
  slot: SlideImageSlot;
  title: string;
  uploadedUtc: string;
}) {
  const url = useAuthedImage(imagePath(sbrId, slot, uploadedUtc));
  return (
    <figure className="am-service-shot">
      <SlideShot url={url} alt={title || 'Service report'} />
      {title && <figcaption>{title}</figcaption>}
    </figure>
  );
}

function SlideShot({ url, alt }: { url: string | null; alt: string }) {
  return (
    <div className="am-backup-frame">
      {url ? <img src={url} alt={alt} /> : <span className="muted small">Loading the screenshot…</span>}
    </div>
  );
}

/** The sidebar of the "Inside the services." slides, with its red chart mark
 *  drawn rather than cropped. */
function ServicesSide({ text }: { text: string }) {
  return (
    <aside className="am-agenda-side">
      <span className="am-chart-mark" aria-hidden="true">
        <svg viewBox="0 0 40 36" width="40" height="36">
          <rect x="2" y="12" width="5" height="18" fill="#ff1a1a" />
          <rect x="10" y="4" width="5" height="26" fill="#ff1a1a" />
          <rect x="18" y="10" width="5" height="20" fill="#ff1a1a" />
          <rect x="26" y="20" width="5" height="10" fill="#ff1a1a" />
          <rect x="0" y="32" width="38" height="3" fill="#ff1a1a" />
        </svg>
      </span>
      <h3>Inside the services.</h3>
      <p>{text}</p>
    </aside>
  );
}

/** The Technical Support slides' shared sidebar, as the template has it. */
function SupportSide() {
  return (
    <aside className="am-agenda-side">
      <img src={securityDiscussion} alt="" />
      <h3>Support Ticket Information</h3>
      <p>Here are your key measurables for the support we provide for you.</p>
    </aside>
  );
}

/**
 * The second Technical Support slide: the template's sidebar and two
 * breakdowns of the client's tickets created in the past three months, live
 * from Autotask — by the employee who raised them, and by issue type.
 *
 * Click a bar for the tickets behind it.
 *
 * "By employee" counts only tickets a person raised (those with a contact):
 * monitoring and automation open tickets with no contact, and they would
 * otherwise crowd out the people. "By issue type" counts every ticket.
 */
function TicketSummarySlide({ clientId }: { clientId: string }) {
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Technical Support</h2>
      <div className="am-agenda-body">
        <SupportSide />
        <TicketBreakdown clientId={clientId} />
      </div>
    </div>
  );
}

/**
 * A Technical Support slide the review added itself (202): the template's
 * sidebar, two screenshots side by side with a description under each, and
 * the slide's takeaways down the right.
 *
 * Everything on it is written on the review form, so a slide added with
 * nothing filled in still shows — with its frames empty, which is what the
 * account manager asked for when they added it.
 */
function AddedSupportSlide({
  sbrId,
  slide,
  images,
}: {
  sbrId: string;
  slide: { pair: number; heading: string; notes: string[]; takeaways: string[] } | undefined;
  images: Partial<Record<SlideImageSlot, SavedSlideImage>>;
}) {
  if (!slide) return null;
  const shots = (SUPPORT_SLIDE_SLOTS[slide.pair] ?? []).map((slot) => ({ slot, image: images[slot] ?? null }));
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Technical Support</h2>
      <div className="am-agenda-body">
        <SupportSide />
        <div className={slide.takeaways.length > 0 ? 'am-support' : 'am-support-main'}>
          {/* flex:1 of its own: with no takeaways beside it this sits in a
              flex column rather than the grid, and the frames would be flat. */}
          <div className="am-support-main am-added-main">
            {slide.heading && <h3>{slide.heading}</h3>}
            <div className="am-service-shots n2">
              {shots.map((shot, i) => (
                <AddedSupportShot
                  key={shot.slot}
                  sbrId={sbrId}
                  slot={shot.slot}
                  image={shot.image}
                  note={slide.notes[i] ?? ''}
                />
              ))}
            </div>
          </div>
          {slide.takeaways.length > 0 && (
            <div className="am-support-takeaways">
              <h3>Takeaways</h3>
              <ul>
                {slide.takeaways.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** One screenshot of an added Technical Support slide, with its description
 *  under it. The frame is drawn whether or not a screenshot was uploaded, so
 *  the two sides of the slide stay the same size. */
function AddedSupportShot({
  sbrId,
  slot,
  image,
  note,
}: {
  sbrId: string;
  slot: SlideImageSlot;
  image: SavedSlideImage | null;
  note: string;
}) {
  const url = useAuthedImage(image ? imagePath(sbrId, slot, image.uploadedUtc) : null);
  return (
    <figure className="am-service-shot">
      <div className="am-backup-frame">
        {image ? (
          url ? (
            <img src={url} alt={image.title || note || 'Screenshot'} />
          ) : (
            <span className="muted small">Loading the screenshot…</span>
          )
        ) : null}
      </div>
      {image?.title && <figcaption>{image.title}</figcaption>}
      {note && <p className="am-shot-note">{note}</p>}
    </figure>
  );
}

/** The past three months, in whole days of this browser, to the end of
 *  today — whole days so the window, and so what is held for it, is the same
 *  every time the slide is shown that day. */
function breakdownWindow() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  to.setDate(to.getDate() + 1);
  const from = new Date(to);
  from.setMonth(from.getMonth() - 3);
  return { from, to };
}

function TicketBreakdown({ clientId }: { clientId: string }) {
  /* Captured once, like slide 5: the three months up to the day it was taken. */
  const cap = useCaptured<TicketSnap>('tickets3m', (refresh) => loadTickets(clientId, breakdownWindow(), refresh), (d) => d.linked);
  const win = useMemo(
    () => (cap.data ? { from: new Date(cap.data.from), to: new Date(cap.data.to) } : breakdownWindow()),
    [cap.data],
  );
  const data = { loading: cap.loading, error: cap.error, data: cap.data };
  const note = (
    <CapturedNote
      capturedUtc={cap.capturedUtc}
      capturedBy={cap.capturedBy}
      canWrite={cap.canWrite}
      refreshing={cap.refreshing}
      onRefresh={() => void cap.refresh()}
    />
  );

  const [drill, setDrill] = useState<{ chart: 'contact' | 'issue'; heading: string; labels: string[] } | null>(null);

  const { created, byContact, byIssue } = useMemo(() => {
    /* The feed also carries tickets only COMPLETED in the window; these
       charts are about tickets created in it. */
    const created = (data.data?.tickets ?? []).filter(
      (t) => t.created !== null && new Date(t.created) >= win.from && new Date(t.created) < win.to,
    );
    const count = (key: (t: ClientTicket) => string | null) => {
      const m = new Map<string, number>();
      for (const t of created) {
        const k = key(t);
        if (k) m.set(k, (m.get(k) ?? 0) + 1);
      }
      return [...m].map(([label, value]) => ({ label, value }));
    };
    return {
      created,
      byContact: count((t) => t.contact),
      byIssue: count((t) => t.issueType || 'No issue type'),
    };
  }, [data.data, win]);

  if (data.loading || data.error || !data.data?.linked) {
    return (
      <div className="am-support-main">
        <h3 className="am-support-h">Ticket Summary</h3>
        <div className="am-support-chart">
          <div className="am-support-chart-label">
            {data.loading ? 'Reading tickets from Autotask…' : data.error ? 'Autotask could not be read' : 'Not found in Autotask'}
          </div>
          {!data.loading && (
            <div className="am-support-chart-note">
              {data.error ?? 'This client was not found in Autotask under its name. Import it from Autotask on Account Management so the names match.'}
            </div>
          )}
          {!data.loading && note}
        </div>
      </div>
    );
  }

  const listed = drill
    ? created
        .filter((t) =>
          drill.labels.includes(drill.chart === 'contact' ? (t.contact ?? '') : t.issueType || 'No issue type'),
        )
        .sort((a, b) => String(b.created).localeCompare(String(a.created)))
    : [];

  return (
    <div className="am-support-main rv-breakdown">
      {note}
      <TicketBarsCard
        title="All Tickets Created Past 3 Months by Employee (non-automation tickets)"
        rows={byContact}
        empty="No tickets raised by people in the past three months."
        onSelect={(heading, labels) => setDrill({ chart: 'contact', heading, labels })}
      />
      <TicketBarsCard
        title="All Tickets Created by Issue Type Past 3 Months"
        rows={byIssue}
        empty="No tickets created in the past three months."
        onSelect={(heading, labels) => setDrill({ chart: 'issue', heading, labels })}
      />
      {drill && (
        <Modal
          title={`${drill.heading} — ${listed.length} ticket${listed.length === 1 ? '' : 's'}, past 3 months`}
          onClose={() => setDrill(null)}
        >
          <div className="table-wrap am-support-drill">
            <table className="table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>{drill.chart === 'contact' ? 'Issue type' : 'Raised by'}</th>
                  <th>Status</th>
                  <th>Assigned</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {listed.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <span className="mono xs">{t.ticketNumber}</span>
                      <div className="xs muted">{t.title}</div>
                    </td>
                    <td>
                      {drill.chart === 'contact'
                        ? t.issueType || <span className="subtle">None</span>
                        : t.contact ?? <span className="subtle">Automation</span>}
                      {drill.labels.length > 1 && (
                        <div className="xs muted">{drill.chart === 'contact' ? t.contact : t.issueType || 'No issue type'}</div>
                      )}
                    </td>
                    <td>{t.status}</td>
                    <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                    <td className="xs">
                      {t.created ? new Date(t.created).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Boxes on one Action Items slide — the template's two columns of three. */
const ACTIONS_PER_PAGE = 6;

/** The template's box colours, in order. Further pages reuse them. */
const ACTION_COLOURS = ['#10263f', '#1b6b24', '#155f80', '#e87331', '#139ed7', '#10263f'];

/** Long descriptions are cut at a word so every box keeps its two lines. */
function shortText(text: string, max = 120): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), 60))}…`;
}

/**
 * The CMIT "Action Items/Recommendations" slide: up to six numbered boxes,
 * filled from the recommendations attached to the review. Each box is the
 * recommendation's title, and its description — or the audit finding behind
 * it when it has no description. Numbering carries on across pages, so the
 * seventh recommendation is 07 on the second page.
 */
/** One box on the Action Items slide: a recommendation or one of the
 *  review's own action items, already reduced to what the box shows. */
interface ActionBox {
  key: string;
  title: string;
  text: string;
}

function ActionItemsSlide({
  items,
  page,
  pages,
}: {
  items: ActionBox[];
  page: number;
  pages: number;
}) {
  const shown = items.slice(page * ACTIONS_PER_PAGE, (page + 1) * ACTIONS_PER_PAGE);
  /* Down the left column first, then the right, as the template numbers them. */
  const left = shown.slice(0, 3);
  const right = shown.slice(3);

  const box = (item: ActionBox, slot: number) => {
    const n = page * ACTIONS_PER_PAGE + slot + 1;
    return (
      <div key={item.key} className="am-action">
        <span className="am-action-num" style={{ background: ACTION_COLOURS[slot] }}>
          {String(n).padStart(2, '0')}
        </span>
        <div className="am-action-body">
          <div className="am-action-title">{item.title}</div>
          <div className="am-action-text">{item.text}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">
        Action Items/Recommendations
        {pages > 1 && (
          <span className="am-action-page">
            {' '}
            ({page + 1} of {pages})
          </span>
        )}
      </h2>
      <div className="am-actions">
        <div className="am-actions-col">{left.map((item, i) => box(item, i))}</div>
        <div className="am-actions-col">{right.map((item, i) => box(item, i + 3))}</div>
      </div>
    </div>
  );
}

/**
 * The CMIT "Security Maturity" slide: the template's sidebar, then a small
 * rose of the chosen assessment, where the client stands, and what is
 * outstanding.
 *
 * Counted the same way as the AM compliance tab —
 * not-assessed stays in the denominator, so the percentage is of everything
 * that applies to them, not of what somebody has got round to.
 */
function SecuritySlide({ assessment }: { assessment: ComplianceAssessment }) {
  const view = useMemo(() => {
    const tally: Record<ResultStatus, number> = {
      met: 0,
      partial: 0,
      not_met: 0,
      not_applicable: 0,
      not_assessed: 0,
    };
    for (const r of assessment.requirements) tally[r.Status] += 1;
    const applicable = tally.met + tally.partial + tally.not_met + tally.not_assessed;

    const byFamily = new Map<number, ComplianceAssessment['requirements']>();
    for (const r of assessment.requirements) {
      const list = byFamily.get(r.FamilyId);
      if (list) list.push(r);
      else byFamily.set(r.FamilyId, [r]);
    }
    const families: RoseFamily[] = assessment.families.map((f) => ({
      FamilyId: f.FamilyId,
      Code: f.Code,
      Title: f.Title,
      cells: (byFamily.get(f.FamilyId) ?? []).map((r) => ({
        RequirementId: r.RequirementId,
        Code: r.Code,
        Title: r.Title,
        Status: r.Status,
      })),
    }));

    /* Not met first, then partly met: the order they get talked about. */
    const outstanding = [
      ...assessment.requirements.filter((r) => r.Status === 'not_met'),
      ...assessment.requirements.filter((r) => r.Status === 'partial'),
    ];

    return {
      tally,
      applicable,
      pct: applicable > 0 ? Math.round((tally.met / applicable) * 100) : null,
      families,
      outstanding,
    };
  }, [assessment]);

  const SHOWN = 5;

  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Security Maturity</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={securityDiscussion} alt="" />
          <h3>Security Discussion</h3>
          <p>
            What are CMIT recommendations to ensure optimal protection of your information and data?
            How can we help you get there?
          </p>
        </aside>
        <div className="am-security">
          <div className="am-security-rose">
            <RoseChart families={view.families} overallPct={view.pct} />
            <RoseLegend tally={view.tally} />
          </div>
          <div className="am-security-detail">
            <div className="am-security-framework">
              {assessment.FrameworkName}
              {assessment.ScopeLevelLabel && <span> · {assessment.ScopeLevelLabel}</span>}
            </div>
            <div className="am-security-stats">
              <div className="stat">
                <div className="label">Controls met</div>
                <div className={`figure${view.pct !== null && view.pct < 50 ? ' flagged' : ''}`}>
                  {view.pct === null ? '—' : `${view.pct}%`}
                </div>
                <div className="foot">
                  {view.tally.met} of {view.applicable} applicable
                </div>
              </div>
              <div className="stat">
                <div className="label">Partly met</div>
                <div className="figure">{view.tally.partial}</div>
                <div className="foot">started, not finished</div>
              </div>
              <div className="stat">
                <div className="label">Not met</div>
                <div className={`figure${view.tally.not_met > 0 ? ' flagged' : ''}`}>
                  {view.tally.not_met}
                </div>
                <div className="foot">gaps to close</div>
              </div>
              <div className="stat">
                <div className="label">Not yet assessed</div>
                <div className="figure">{view.tally.not_assessed}</div>
                <div className="foot">
                  {assessment.Status === 'complete' ? 'assessment complete' : 'assessment in progress'}
                </div>
              </div>
            </div>

            <h4>What is outstanding ({view.outstanding.length})</h4>
            {view.outstanding.length === 0 ? (
              <p className="small muted">
                Nothing outstanding — every applicable control assessed so far is met.
              </p>
            ) : (
              <ul className="am-security-gaps">
                {view.outstanding.slice(0, SHOWN).map((r) => (
                  <li key={r.RequirementId}>
                    <span className={`badge ${r.Status === 'not_met' ? 'badge-critical' : 'badge-warning'}`}>
                      {r.Status === 'not_met' ? 'Not met' : 'Partly met'}
                    </span>
                    <span className="mono xs subtle">{r.Code}</span> {r.Title}
                  </li>
                ))}
                {view.outstanding.length > SHOWN && (
                  <li className="xs muted">
                    and {view.outstanding.length - SHOWN} more — the full list is on the Compliance tab
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The agenda slide, as the CMIT business-review template has it. Fixed text:
 * it is the standing shape of every review, not something filled in per
 * client. Listed down the left column then the right, the order the template
 * reads in.
 */
const AGENDA: { title: string; text: string; icon: string }[] = [
  {
    title: 'What’s New',
    text: 'Sharing news regarding new or updated products, services, our teams and our business.',
    icon: whatsNewIcon,
  },
  {
    title: 'Business Discussion',
    text: 'Discussion of current and future topics and events within the business to ensure technology and technology support is met and will meet requirements for optimal business operations.',
    icon: discussionIcon,
  },
  {
    title: 'Technical Support',
    text: 'Numbers and status for the support services delivered by CMIT Solutions technology teams.',
    icon: supportIcon,
  },
  {
    title: 'Asset Management',
    text: 'Review your IT assets and needs.',
    icon: assetsIcon,
  },
  {
    title: 'IT Services Review',
    text: 'Checkpoint review of services in place as of this review.',
    icon: servicesIcon,
  },
  {
    title: 'IT Projects',
    text: 'Status of current or upcoming IT projects (if applicable).',
    icon: projectsIcon,
  },
  {
    title: 'Security Review',
    text: 'Discuss the importance of achieving security maturity and how to achieve it.',
    icon: securityIcon,
  },
  {
    title: 'Action Items/Recommendations',
    text: 'Review list of outstanding or future action items and recommendations.',
    icon: actionsIcon,
  },
];

/**
 * The "What's new" slide's three groups, as the CMIT template lays them out.
 * People uses the clock, as the template does. `fallback` is the heading of a
 * line where only the description was filled in.
 */
const WHATS_NEW: { key: WhatsNewKey; heading: string; icon: string; fallback: string }[] = [
  { key: 'product', heading: 'Products', icon: newProductIcon, fallback: 'New product' },
  { key: 'service', heading: 'Services', icon: newServiceIcon, fallback: 'New service' },
  { key: 'people', heading: 'People', icon: newServiceIcon, fallback: 'New team member' },
];

/**
 * The CMIT template's second-to-last page: red, the navy rule and corner, "Your
 * technology team" over "Questions?", and the copyright along the foot.
 */
function QuestionsSlide() {
  return (
    <div className="am-slide am-slide-questions">
      <span className="am-questions-corner" aria-hidden="true" />
      <div className="am-questions-body">
        <div className="am-questions-eyebrow">Your technology team</div>
        <h2 className="am-questions-title">Questions?</h2>
      </div>
      <div className="am-questions-foot">© CMIT Solutions LLC {new Date().getFullYear()}</div>
    </div>
  );
}

/**
 * The CMIT template's last page: the template's own artwork, "Next steps —
 * Thank you for your time." and the presenter's contact lines (migration 148),
 * each left off when there is nothing to show.
 */
/** The thank-you slide's website when a review gives none. */
export const DEFAULT_CLOSING_WEBSITE = 'www.cmitsolutions.com';

function ThankYouSlide({ email, phone, website }: { email: string | null; phone: string | null; website: string }) {
  return (
    <div className="am-slide am-slide-thanks" style={{ backgroundImage: `url(${thankYouArt})` }}>
      <span className="am-thanks-corner am-thanks-corner-tr" aria-hidden="true" />
      <span className="am-thanks-corner am-thanks-corner-bl" aria-hidden="true" />
      <div className="am-thanks-body">
        <span className="am-thanks-rule" aria-hidden="true" />
        <div className="am-thanks-eyebrow">Next steps</div>
        <h2 className="am-thanks-title">Thank you for your time.</h2>
        <div className="am-thanks-contact">
          <div className="am-thanks-contact-h">Contact Information:</div>
          {email && <div>{email}</div>}
          {phone && <div>{phone}</div>}
          {website && <div>{website}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * The CMIT "User Feedback" slide: the template's Feedback sidebar, a large
 * screenshot in the middle and a smaller one in the upper right.
 */
function FeedbackSlide({
  sbrId,
  clientId,
  main,
  side,
}: {
  sbrId: string;
  clientId: string;
  main: { slot: SlideImageSlot; title: string; uploadedUtc: string } | null;
  side: { slot: SlideImageSlot; title: string; uploadedUtc: string } | null;
}) {
  /* Captured once, on the review: the twelve months up to the day it was
     taken, shown as they were from then on — until Refresh. */
  const cap = useCaptured<CsatSnap>('csat12m', (refresh) => loadCsat(clientId, csatWindow(), refresh), (d) => d.linked);
  const d = cap.data;
  const live = d?.linked ?? false;
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">User Feedback</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={securityDiscussion} alt="" />
          <h3>Feedback</h3>
          <p>CMIT Solutions sends periodic surveys to users and we welcome and encourage customer feedback.</p>
          <p>We share positive feedback with the entire team.</p>
          <p>We evaluate critical feedback for continuous improvement throughout our organization.</p>
        </aside>
        {live && d ? (
          <div className="am-csat">
            <div className="am-csat-main">
              <CapturedNote
                capturedUtc={cap.capturedUtc}
                capturedBy={cap.capturedBy}
                canWrite={cap.canWrite}
                refreshing={cap.refreshing}
                onRefresh={() => void cap.refresh()}
              />
              {d.responses.length === 0 ? (
                <p className="am-csat-empty">No survey responses from {d.companyName || 'this client'} in the last twelve months.</p>
              ) : (
                <div className="am-csat-table-wrap">
                  <CsatTable rows={d.responses} />
                </div>
              )}
            </div>
            <div className="am-csat-side">
              <div className="am-csat-side-title">Avg Survey Score Past 12 Mo</div>
              <div className="am-csat-side-body">
                <CsatDial value={d.average} />
                <span className="am-csat-side-label">Company</span>
              </div>
              <div className="am-csat-side-foot">
                {d.count} response{d.count === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        ) : cap.loading ? (
          <div className="am-csat-empty">Reading the survey results from Autotask…</div>
        ) : main || side ? (
          /* Not linked to Autotask (or it could not be read): the screenshots uploaded on the review. */
          <div className={`am-feedback${side ? ' has-side' : ''}`}>
            {main && (
              <div className="am-feedback-main">
                <ServiceShot sbrId={sbrId} {...main} />
              </div>
            )}
            {side && (
              <div className="am-feedback-side">
                <ServiceShot sbrId={sbrId} {...side} />
              </div>
            )}
          </div>
        ) : (
          <div className="am-csat-empty">
            {cap.error ?? 'This client is not linked to an Autotask company, so there are no survey results to show.'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Slide 11 — Services Provided, as the CMIT template lays it out: the "What
 * value are you receiving?" sidebar, and every service on the client's active
 * Autotask contracts with its units — no prices, no contract names (see
 * ServicesProvided.tsx). Captured on the review and kept until Refresh.
 */
function ServicesProvidedSlide({ clientId }: { clientId: string }) {
  const cap = useCaptured<ServicesSnap>('services', (refresh) => loadServicesProvided(clientId, refresh), (d) => d.linked);
  const d = cap.data;
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Services Provided</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={itMark} alt="" />
          <h3>What value are you receiving?</h3>
          <p>CMIT Solutions business reviews are intended to:</p>
          <ul>
            <li>Evaluate the current state of support and technology</li>
            <li>Review ongoing business initiatives related to technology</li>
          </ul>
        </aside>
        <div className="am-svc-body">
          {d?.linked && (
            <CapturedNote
              capturedUtc={cap.capturedUtc}
              capturedBy={cap.capturedBy}
              canWrite={cap.canWrite}
              refreshing={cap.refreshing}
              onRefresh={() => void cap.refresh()}
            />
          )}
          {cap.loading && !d ? (
            <div className="am-csat-empty">Reading the client's contracts from Autotask…</div>
          ) : d?.linked ? (
            d.services.length === 0 ? (
              <div className="am-csat-empty">No services with a quantity on an active contract.</div>
            ) : (
              <ServicesGrid services={d.services} />
            )
          ) : (
            <div className="am-csat-empty">
              {cap.error ?? 'This client is not linked to an Autotask company, so there are no contracts to show.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Slide 13 — Active IT Projects, as the CMIT template lays it out: the
 * "Project Information" sidebar, and the client's open Autotask projects (see
 * ActiveProjects.tsx). Captured on the review and kept until Refresh.
 */
/**
 * Slide 10a, Agent Health: how many of the client's machines are checking in,
 * patched and protected (migration 186).
 *
 * THE FIGURES ARE THE CLIENT'S OWN AGENTS, judged by exactly the rules NOC ›
 * Estate health uses on the whole estate — one function decides what
 * "offline" and "unpatched" mean (api lib/noc/agentHealth.ts), because the
 * number on this slide is the one a client reads back to us.
 *
 * Captured with the rest of the deck, so the review keeps the figures from
 * the day it was given rather than quietly restating them months later.
 *
 * A client whose Automate client is not mapped gets the slide saying so,
 * not a slide of zeros.
 *
 * AN UPLOADED SCREENSHOT WINS. Somebody put it on the review to be shown —
 * most often for a client Automate does not know — so when there is one the
 * slide shows it, with its caption, in place of the live figures.
 */
function AgentHealthSlide({
  clientId,
  sbrId,
  shot,
}: {
  clientId: string;
  sbrId: string;
  shot: { slot: SlideImageSlot; title: string; uploadedUtc: string } | null;
}) {
  const cap = useCaptured<AgentHealthSnap>(
    'agentHealth',
    (refresh) => loadAgentHealth(clientId, refresh),
    (d) => d.linked,
  );
  const d = cap.data;
  const showing = !shot && Boolean(d?.linked && d.totals.agents > 0);
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Agent Health</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={securityDiscussion} alt="" />
          <h3>Your Machines</h3>
          <p>Are they checking in, patched and protected?</p>
        </aside>
        <div className="am-svc-body">
          {showing && (
            <CapturedNote
              capturedUtc={cap.capturedUtc}
              capturedBy={cap.capturedBy}
              canWrite={cap.canWrite}
              refreshing={cap.refreshing}
              onRefresh={() => void cap.refresh()}
            />
          )}
          {shot ? (
            <ServiceShot sbrId={sbrId} {...shot} />
          ) : cap.loading && !d ? (
            <div className="am-csat-empty">Reading the client&apos;s agents from Automate…</div>
          ) : showing && d ? (
            <>
              <AgentHealthFigures data={d} />
              <h3 className="am-ah-heading">Needs attention</h3>
              <AgentAttentionList agents={d.attention} limit={6} total={d.attentionTotal} />
            </>
          ) : (
            <AgentHealthEmpty data={d ?? null} error={cap.error} />
          )}
        </div>
      </div>
    </div>
  );
}

function ActiveProjectsSlide({ clientId }: { clientId: string }) {
  const cap = useCaptured<ProjectsSnap>('projects', (refresh) => loadActiveProjects(clientId, refresh), (d) => d.linked);
  const d = cap.data;
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Active IT Projects</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={securityDiscussion} alt="" />
          <h3>Project Information</h3>
          <p>Where are we at with your current projects?</p>
        </aside>
        <div className="am-svc-body">
          {d?.linked && (
            <CapturedNote
              capturedUtc={cap.capturedUtc}
              capturedBy={cap.capturedBy}
              canWrite={cap.canWrite}
              refreshing={cap.refreshing}
              onRefresh={() => void cap.refresh()}
            />
          )}
          {cap.loading && !d ? (
            <div className="am-csat-empty">Reading the client&apos;s projects from Autotask…</div>
          ) : d?.linked ? (
            d.projects.length === 0 ? (
              <div className="am-csat-empty">No active projects right now.</div>
            ) : (
              <ProjectsList projects={d.projects} />
            )
          ) : (
            <div className="am-csat-empty">
              {cap.error ?? 'This client is not linked to an Autotask company, so there are no projects to show.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** The width every slide is laid out at in Presentation mode, then scaled. */
const STAGE_W = 1280;

/**
 * The slides' wrapper. In the normal view it is just a wrapper. In
 * Presentation mode the slide is laid out at a fixed 1280px wide — the size
 * its text and spacing were drawn for — and scaled up or down to fill the
 * window, so it looks the same on a laptop as on a wall screen. The window
 * also asks the browser for full screen; leaving full screen (Esc) leaves
 * Presentation mode too.
 */
function PresentingStage({ presenting, slide, children }: { presenting: boolean; slide: number; children: React.ReactNode }) {
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    if (!presenting) return;
    const fit = () => {
      const el = inner.current;
      if (!el) return;
      const h = Math.max(el.scrollHeight, (STAGE_W * 9) / 16);
      setScale(Math.min(window.innerWidth / STAGE_W, window.innerHeight / h) * 0.98);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (inner.current) ro.observe(inner.current);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [presenting, slide]);

  if (!presenting) return <>{children}</>;
  return (
    <div className="am-stage">
      <div ref={inner} className="am-stage-inner" style={{ width: STAGE_W, transform: `translate(-50%, -50%) scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Presentation mode's own controls: the browser's full screen while it lasts,
 * and a slim bar at the foot — previous, where we are, next, exit — that shows
 * when the mouse moves and fades when it rests.
 */
function PresentingChrome({ slide, total, onGo, onExit }: { slide: number; total: number; onGo: (d: number) => void; onExit: () => void }) {
  const [awake, setAwake] = useState(true);
  /* Held in a ref: the parent passes a new function every render, and the
     full-screen effect below must run once, not on each of them. */
  const exit = useRef(onExit);
  exit.current = onExit;

  useEffect(() => {
    /* Full screen where the browser allows it; the deck covers the window either way. */
    const root = document.documentElement;
    let went = false;
    if (!document.fullscreenElement && root.requestFullscreen) {
      root.requestFullscreen().then(
        () => {
          went = true;
        },
        () => undefined,
      );
    }
    const onChange = () => {
      if (!document.fullscreenElement && went) exit.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) exit.current();
    };
    document.addEventListener('fullscreenchange', onChange);
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    let timer = window.setTimeout(() => setAwake(false), 2500);
    const wake = () => {
      setAwake(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setAwake(false), 2500);
    };
    window.addEventListener('mousemove', wake);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('mousemove', wake);
    };
  }, []);

  return (
    <div className={`am-present-bar${awake ? ' is-awake' : ''}`}>
      <button type="button" disabled={slide === 0} onClick={() => onGo(-1)} aria-label="Previous slide">
        ←
      </button>
      <span>
        {slide + 1} / {total}
      </span>
      <button type="button" disabled={slide === total - 1} onClick={() => onGo(1)} aria-label="Next slide">
        →
      </button>
      <button type="button" className="am-present-exit" onClick={() => exit.current()}>
        Exit presentation mode
      </button>
    </div>
  );
}

/* The template's warranty dials: Contract renewals belongs to Services, not
   to the hardware this slide is about. */
const SLIDE_WARRANTY_KINDS = KINDS.filter((k) => k.kind !== 'contract');

/**
 * Slide 10's first page — Asset Management as the CMIT template lays it out:
 * the "IT Assets" sidebar, then Warranty, Needs Attention and Hardware
 * Recommendations, each under its icon. Warranty is the client page's
 * warranty dials (AmWarranties.tsx) less Contract renewals, captured on the
 * review once Autotask answers and kept until Refresh. A dial opens the items
 * behind it in place of the two sections below.
 */
function AssetOverviewSlide({ clientId }: { clientId: string }) {
  const cap = useCaptured<WarrantiesSnap>('warranties', (refresh) => loadWarranties(clientId, refresh), (d) => d.linked);
  const d = cap.data;
  const [focus, setFocus] = useState<Kind | null>(null);
  const focused = SLIDE_WARRANTY_KINDS.find((k) => k.kind === focus);
  const drilled = focus
    ? (d?.items ?? [])
        .filter((i) => i.kind === focus)
        .sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999') || a.name.localeCompare(b.name))
    : [];
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">Asset Management</h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={assetsIcon} alt="" className="am-asset-icon" />
          <h3>IT Assets</h3>
          <p>Review current inventory and needs.</p>
        </aside>
        <div className="am-ao">
          <section className="am-ao-section">
            <div className="am-ao-head">
              <h3>Warranty</h3>
              {d?.linked && (
                <CapturedNote
                  capturedUtc={cap.capturedUtc}
                  capturedBy={cap.capturedBy}
                  canWrite={cap.canWrite}
                  refreshing={cap.refreshing}
                  onRefresh={() => void cap.refresh()}
                />
              )}
            </div>
            <div className="am-ao-row">
              <span className="am-agenda-icon">
                <img src={warrantyIcon} alt="" />
              </span>
              {cap.loading && !d ? (
                <div className="am-csat-empty">Reading the client&apos;s warranties from Autotask…</div>
              ) : d?.linked ? (
                <div className="am-ao-gauges">
                  {SLIDE_WARRANTY_KINDS.map(({ kind, label, noun }) => {
                    const { mine, value, hint } = dialFor(d.items, kind, noun);
                    return (
                      <Gauge
                        key={kind}
                        label={label}
                        value={value}
                        target={100}
                        hint={hint}
                        onClick={mine.length ? () => setFocus(focus === kind ? null : kind) : undefined}
                        drillLabel={focus === kind ? 'Close' : 'Show these'}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="am-csat-empty">
                  {cap.error ?? 'This client is not linked to an Autotask company, so there are no warranties to show.'}
                </div>
              )}
            </div>
          </section>

          {focused ? (
            <section className="am-ao-section am-ao-drill">
              <div className="am-ao-head">
                <h3>{focused.label}</h3>
                <button type="button" className="link-button no-print" onClick={() => setFocus(null)}>
                  Close
                </button>
              </div>
              <ul className="am-ao-list">
                {drilled.map((i) => {
                  const state = stateOf(i.date);
                  const days = i.date ? daysUntil(i.date) : null;
                  return (
                    <li key={i.key} className={`is-${state}`}>
                      <span className="am-ao-name">
                        <strong>{i.name}</strong>
                        {i.detail && <span>{i.detail}</span>}
                      </span>
                      <span className="am-ao-date">
                        {shortDay(i.date)}
                        {days !== null && state === 'expired' && <span>expired {Math.abs(days)} days ago</span>}
                        {days !== null && state === 'soon' && <span>in {days} days</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : (
            <>
              <section className="am-ao-section">
                <h3>Needs Attention</h3>
                <span className="am-agenda-icon">
                  <img src={alarmIcon} alt="" />
                </span>
              </section>
              <section className="am-ao-section">
                <h3>Hardware Recommendations</h3>
                <span className="am-agenda-icon">
                  <img src={alarmIcon} alt="" />
                </span>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Slide 10 — Asset Management, as the CMIT template lays it out: the "IT
 * Assets" sidebar, and one page of the client's active assets by category
 * (see AssetSlides.tsx). As many of these slides as the assets need; the
 * capture and Refresh belong to the deck, which pages them.
 */
function AssetManagementSlide({
  snap,
  lines,
  page,
  pages,
  error,
  note,
}: {
  snap: AssetsSnap | null;
  lines: AssetLine[];
  page: number;
  pages: number;
  error: string | null;
  note: React.ReactNode;
}) {
  return (
    <div className="am-slide am-slide-agenda">
      <span className="am-agenda-corner" aria-hidden="true" />
      <img className="am-agenda-logo" src={cmitLogoLight} alt="CMIT Solutions — Your Technology Team" />
      <h2 className="am-agenda-title">
        Asset Management{pages > 1 && <span className="am-asset-page"> ({page + 1} of {pages})</span>}
      </h2>
      <div className="am-agenda-body">
        <aside className="am-agenda-side">
          <img src={assetsIcon} alt="" className="am-asset-icon" />
          <h3>IT Assets</h3>
          <p>Review current inventory and needs.</p>
        </aside>
        <div className="am-svc-body">
          {snap?.linked && page === 0 && note}
          {error ? (
            <div className="am-csat-empty">{error}</div>
          ) : !snap ? (
            <div className="am-csat-empty">Reading the client&apos;s assets from Autotask…</div>
          ) : !snap.linked ? (
            <div className="am-csat-empty">This client is not linked to an Autotask company, so there are no assets to show.</div>
          ) : lines.length === 0 ? (
            <div className="am-csat-empty">No active assets in Autotask.</div>
          ) : (
            <AssetsTable lines={lines} />
          )}
        </div>
      </div>
    </div>
  );
}
